'use client';

import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';

interface ExpandableSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  expandedWidth?: number | string;
  forceOpen?: boolean;
}

function ExpandableSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  expandedWidth = 280,
  forceOpen = false,
}: ExpandableSearchInputProps) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpen = forceOpen || open;

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleClose = () => {
    if (forceOpen) return;
    setOpen(false);
    setFocused(false);
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    setFocused(false);
    if (!value && !forceOpen) setOpen(false);
  };

  const label = ariaLabel ?? placeholder;

  return (
    <motion.div
      className={cn('relative flex h-10 items-center overflow-hidden rounded-[10px]', className)}
      animate={{
        width: isOpen ? expandedWidth : 40,
        borderColor: isOpen
          ? focused
            ? 'var(--v2-bg-blue-50)'
            : 'var(--v2-btn-secondary-hover)'
          : 'transparent',
      }}
      style={{ border: '1px solid transparent' }}
      transition={{ type: 'spring', damping: 22, stiffness: 280, mass: 0.8 }}
    >
      {!isOpen && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={label}
          className="text-muted-foreground flex h-10 w-10 cursor-pointer items-center justify-center transition-colors hover:text-txt-1"
        >
          <Icon name="Search" size="24px" className="flex-shrink-0" />
        </button>
      )}

      {isOpen && (
        <>
          <div className="text-muted-foreground pointer-events-none absolute left-[14px] z-10 flex h-full items-center">
            <Icon name="Search" size="20px" className="flex-shrink-0" />
          </div>

          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === 'Escape' && handleClose()}
            placeholder={placeholder}
            aria-label={label}
            className="body-small h-full w-full bg-transparent pl-[42px] pr-[42px] text-txt-1 outline-none placeholder:text-txt-3 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          />

          {value.length > 0 && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              aria-label="Clear search"
              className="absolute right-[14px] z-10 flex cursor-pointer items-center text-txt-3 transition-colors hover:text-txt-1"
            >
              <Icon name="Close" size="20px" />
            </button>
          )}
        </>
      )}
    </motion.div>
  );
}

export type { ExpandableSearchInputProps };
export { ExpandableSearchInput };
